import { useLayoutEffect, useMemo, useRef, useState } from "react";
import FormatDateTime from "../Functions/FormatDate";
import { useNavigate, useParams } from "react-router-dom";
import { IoChatboxOutline } from "react-icons/io5";
import DeleteItemModal from "../Components/DeleteItemModal";
import { LuChevronDown, LuChevronUp, LuTrash2 } from "react-icons/lu";
import { BiDownload } from "react-icons/bi";
import Spinner from "../Components/Spinner";
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const ModelsCell = ({ models }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const containerRef = useRef(null);

    const checkOverflow = () => {
        const el = containerRef.current;
        if (el) {
            const overflow = el.scrollHeight > 38;
            setHasMore(overflow);
        }
    };

    useLayoutEffect(() => {
        checkOverflow();

        const resizeObserver = new ResizeObserver(() => checkOverflow());
        if (containerRef.current) resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, [models]);

    if (!models || models.length === 0) return null;

    return (
        <div className="custom_cell_org_wrapper">

            <div
                ref={containerRef}
                className={`custom_cell_org_list ${isExpanded ? 'custom_cell_org_list_expanded' : ''}`}
            >
                {hasMore && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="org_list_toggle"
                        type="button"
                    >
                        {isExpanded ? <LuChevronUp /> : <LuChevronDown />}
                    </button>
                )}
                {models.map((item) => (
                    <div key={item} className="custom_cell_org_pill">
                        <span className="org_pill_name" title={item}>{item}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ChatHistoryTable = ({ account_id, data, fetchChatHistory }) => {
    const {t} = useLanguage()
    const navigate = useNavigate()
    const { org_id } = useParams()
    const [openDeleteItem, setOpenDeleteItem] = useState(false)
    const [selectedItem, setSelectedItem] = useState({})
    const [loadingDownload, setLoadingDownload] = useState(false)

    const headers = useMemo(() => {
        return [t.title, t.chat_history.used_models, t.created_at, t.sidebar.actions];
    }, []);

    const handleNavigate = (chat_id) => {
        navigate(`/e8console/organizations/${org_id}/playground/${chat_id}`)
    }

    const downloadItem = async (item) => {
        setLoadingDownload(true)
        try {
            const res = await fetch(`${GEN_BACKEND_LINK}/get_chat_download`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id: account_id,
                    chat_id: item.id,
                }),
            });

            const blob = await res.blob();

            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `${item.name || "chat"}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error("Download error:", err);
            setLoadingDownload(false)
        } finally {
            setLoadingDownload(false)
        }
    };

    const fetchData = () => {
        fetchChatHistory(org_id)
    }

    return (
        <div className="custom_table_container fade-in">
            <table className="custom_table">
                <thead className="custom_table_head">
                    <tr className="custom_table_row">
                        {headers.map((header, index) => (
                            <th key={index} className="custom_table_th">
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="custom_table_body">
                    {data.length > 0 ? (
                        data.map((item, rowIndex) => (
                            <tr key={item.id || rowIndex} className="custom_table_row">

                                <td className="custom_table_td td_user td_cursor" onClick={() => handleNavigate(item.id)}>
                                    <i className="td_icon"><IoChatboxOutline /></i>
                                    {item.name}
                                </td>

                                <td className="custom_table_td td_orgs">
                                    <ModelsCell models={item.used_models} />
                                </td>


                                <td className="custom_table_td td_orgs">
                                    {FormatDateTime(item.created_at)}
                                </td>
                                <td className="custom_table_td td_orgs">
                                    <button
                                        className="remove_file_btn"
                                        onClick={() => {
                                            setSelectedItem(item);
                                            setOpenDeleteItem(true);
                                        }}
                                        title="Delete"
                                    >
                                        <LuTrash2 />
                                    </button>
                                    {loadingDownload && selectedItem == item ? (
                                        <button className="download_file_btn">
                                            <Spinner loading={true} />
                                        </button>
                                    ) : (
                                        <button
                                            className="download_file_btn"
                                            onClick={() => { downloadItem(item); setSelectedItem(item) }}
                                            title="Download file"
                                        >
                                            <BiDownload />
                                        </button>
                                    )}
                                </td>

                            </tr>
                        ))
                    ) : (
                        <tr className="custom_table_row_empty">
                            <td colSpan={headers.length} className="custom_table_td_empty">
                                {t.no_data_found}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            <DeleteItemModal
                account_id={account_id}
                show={openDeleteItem}
                handleClose={() => setOpenDeleteItem(false)}
                fetchRecords={fetchData}
                item={selectedItem}
                endpoint={"delete_chat"}
                title={t.chat_history.delete_title}
                description={t.chat_history.delete_warning}
                input_value={selectedItem}
            />
        </div>
    );
};

export default ChatHistoryTable;